// メモ間の影響関係 — 自分のメモ同士の系譜（思想の織物）
import { escapeHtml, screenHead, showScreen, showLoading, hideLoading, toast, renderHistorySection, bindHistoryHandlers } from '../ui.js';
import { fetchMemoLineage } from '../api.js';
import { getAllBooks, saveAiHistory, getAiHistoryByType } from '../cache.js';

export async function showMemoLineageScreen() {
  const histItems = await getAiHistoryByType('memo-lineage');
  renderEntry(histItems);
}

function renderEntry(histItems) {
  const html = `
    ${screenHead('メモ間の影響関係')}
    <p style="color:var(--dim);font-size:13px;line-height:1.75;margin:0 0 20px;">
      時系列のメモを読み込み、「メモAが後のメモBの中で発展している」関係を AI が検出します。<br/>
      系譜・影響関係の「自分版」。あなたの思想の織物が見えます。
    </p>
    <button class="btn btn--ink btn--block" id="btn-go" type="button">
      <svg class="icon"><use href="#i-sparkles"/></svg>
      <span>思想の織物を見る</span>
    </button>
    <p style="margin-top:14px;font-size:11px;color:var(--mute);line-height:1.6;font-family:var(--font-mono);letter-spacing:0.08em;">
      使用モデル: claude-opus-5-5 · 1 回 約 6-15 円
    </p>
    ${renderHistorySection(histItems)}
  `;
  const screen = showScreen('memo-lineage', html);
  screen.querySelector('[data-action="back"]').addEventListener('click', () => window.history.back());
  screen.querySelector('#btn-go').addEventListener('click', runLineage);
  bindHistoryHandlers(
    screen,
    histItems,
    (entry) => renderResult(entry.payload),
    () => showMemoLineageScreen()
  );
}

async function runLineage() {
  const all = await getAllBooks();
  const eligible = all.filter((b) => (b.note || '').trim().length > 200 && b.endDate);
  if (eligible.length < 3) {
    toast('メモ＋読了日のある書籍が 3 冊以上必要です');
    return;
  }
  showLoading('AI が思想の系譜を辿っています…');
  try {
    const payload = eligible.map((b) => ({
      id: b.id, name: b.name, endDate: b.endDate, note: b.note,
    }));
    const result = await fetchMemoLineage(payload);
    // book id → name の辞書を payload にも保存（描画用）
    const bookMap = Object.fromEntries(eligible.map((b) => [b.id, { name: b.name, endDate: b.endDate }]));
    const enriched = { ...result, bookMap };
    await saveAiHistory({
      type: 'memo-lineage',
      title: `思想の織物 (${result.edges?.length || 0} 関係 · ${result.threads?.length || 0} 糸)`,
      subtitle: (result.threads || []).slice(0, 2).map((t) => t.theme).join(' / '),
      context: {},
      payload: enriched,
    });
    renderResult(enriched);
  } catch (e) {
    console.error(e);
    toast('検出に失敗しました');
  } finally {
    hideLoading();
  }
}

export function renderFromHistory(entry) {
  renderResult(entry.payload);
}

function renderResult(data) {
  const edges = data.edges || [];
  const threads = data.threads || [];
  const bookMap = data.bookMap || {};
  const nameOf = (id) => bookMap[id]?.name || id;

  const html = `
    ${screenHead('メモ間の影響関係')}

    ${threads.length ? `
      <p class="section-label">threads · 思想の糸</p>
      ${threads.map((t) => `
        <article class="thread-card">
          <h3 class="thread-card__theme">${escapeHtml(t.theme || '')}</h3>
          <p class="thread-card__summary">${escapeHtml(t.summary || '')}</p>
          <div class="thread-card__sequence">
            ${(t.sequence || []).map((id, i, arr) => `
              <span class="thread-card__node">${escapeHtml(nameOf(id))}</span>
              ${i < arr.length - 1 ? '<span class="thread-card__arrow">→</span>' : ''}
            `).join('')}
          </div>
        </article>
      `).join('')}
    ` : ''}

    ${edges.length ? `
      <p class="section-label" style="margin-top:32px;">influences · 個々の影響</p>
      <ul class="memo-edges">
        ${edges.map((e) => {
          const kindJa = e.kind === '反転' ? '⇄' : e.kind === '統合' ? '⊕' : e.kind === '引用' ? '「」' : '→';
          return `
            <li class="memo-edges__item">
              <div class="memo-edges__head">
                <span class="memo-edges__from">${escapeHtml(nameOf(e.from))}</span>
                <span class="memo-edges__arrow">${escapeHtml(kindJa)} ${escapeHtml(e.kind || '影響')}</span>
                <span class="memo-edges__to">${escapeHtml(nameOf(e.to))}</span>
              </div>
              ${e.reason ? `<p class="memo-edges__reason">${escapeHtml(e.reason)}</p>` : ''}
            </li>
          `;
        }).join('')}
      </ul>
    ` : ''}

    <button class="btn btn--block btn--ghost" id="btn-regen" type="button" style="margin-top:24px;">
      <svg class="icon"><use href="#i-refresh"/></svg>
      <span>再検出する</span>
    </button>
  `;
  const screen = showScreen('memo-lineage', html);
  screen.querySelector('[data-action="back"]').addEventListener('click', () => window.history.back());
  screen.querySelector('#btn-regen').addEventListener('click', () => showMemoLineageScreen());
}
